import { CollapsibleSection } from "components/collapsibleSection";
import { Label } from "components/label";
import { SwitchButton } from "components/switchButton";
import { Tag } from "components/tag";
import {
  ApplyToLevel,
  ApplyToProductType,
  DiscountType,
  IPromotionConditionDetail,
  UserType,
} from "interfaces";
import { isEmpty } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import {
  canAccessByType,
  formatNumber,
  getApplyToLevelLabel,
  getApplyToProductTypeLabel,
  getDiscountTypeLabel,
  getFormattedDateTime,
} from "utills/helpers";

export const ViewConditionSetting = ({
  conditionDetailData,
}: {
  conditionDetailData: IPromotionConditionDetail;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const shouldShowEventName = () => {
    const applyToProductTypesWithEventName = [
      ApplyToProductType.INCLUDE,
      ApplyToProductType.EXCLUDE,
    ];

    return applyToProductTypesWithEventName.includes(
      conditionDetailData?.apply_to_product_type
    );
  };

  const shouldShowApplyToLevel = () => {
    const discountTypesWithApplyToLevel = [
      DiscountType.PERCENT,
      DiscountType.FIX_PRICE,
      DiscountType.CAMPAIGN_DISCOUNT,
    ];
    return discountTypesWithApplyToLevel.includes(
      conditionDetailData?.discount_type_display
    );
  };

  const shouldShowPercentDiscountValue = (
    discountType: DiscountType | null
  ) => {
    const discountTypeForDiscountValue: any[] = [DiscountType.PERCENT];
    return discountTypeForDiscountValue.includes(discountType);
  };
  const shouldShowFixedAmountDiscountValue = (
    discountType: DiscountType | null
  ) => {
    const discountTypeForDiscountValue: any[] = [DiscountType.FIX_PRICE];
    return discountTypeForDiscountValue.includes(discountType);
  };

  const shouldShowMinOrderValue = () => {
    return conditionDetailData?.apply_to_level === ApplyToLevel.TOTAL_ORDER;
  };

  const shouldShowMaxApplicableItems = () => {
    return false;
    // return conditionDetailData?.apply_to_level === ApplyToLevel.PER_ITEM;
  };

  const shouldShowQuantityDiscountRules = () => {
    const discountTypeForQuantityDiscountRules = [
      DiscountType.GROUP_DISCOUNT,
      DiscountType.QUANTITY_DISCOUNT,
    ];

    return discountTypeForQuantityDiscountRules.includes(
      conditionDetailData?.discount_type_display
    );
  };

  const shouldShowCampaignDiscount = () => {
    const discountTypeForCampaignDiscount = [DiscountType.CAMPAIGN_DISCOUNT];

    return discountTypeForCampaignDiscount.includes(
      conditionDetailData?.discount_type_display
    );
  };

  const shouldShowContentByEventCurrency = (currency: string) => {
    const uniqueCurrencies = (conditionDetailData?.products ?? [])
      .map((obj) => obj.currency)
      .filter(
        (currency, index, array) =>
          typeof currency === "string" && array.indexOf(currency) === index
      );

    return uniqueCurrencies.includes(currency);
  };

  const shouldShowTickets = () => {
    const applyToProductTypesWithTickets = [
      ApplyToProductType.INCLUDE,
      ApplyToProductType.EXCLUDE,
    ];

    return applyToProductTypesWithTickets.includes(
      conditionDetailData?.apply_to_product_type
    );
  };

  const _renderDiscountFields = (discountType: DiscountType | null) => {
    return (
      <>
        {shouldShowPercentDiscountValue(discountType) && (
          <>
            <div className="col-span-full sm:col-span-2">
              <Label id="discount_value" name="Discount Value" />
              <div className="relative">
                <p>{conditionDetailData?.discount_value}%</p>
              </div>
            </div>
            <div className="col-span-full sm:col-span-2">
              <Label id="max_discount_value_usd" name="Max discount amount" />
              {conditionDetailData?.max_discount_value ||
              conditionDetailData?.max_discount_value_usd ? (
                <p>
                  {conditionDetailData?.max_discount_value
                    ? `${formatNumber(
                        conditionDetailData?.max_discount_value
                      )}đ`
                    : ""}{" "}
                  {conditionDetailData?.max_discount_value &&
                  conditionDetailData?.max_discount_value_usd
                    ? "|"
                    : ""}{" "}
                  {conditionDetailData?.max_discount_value_usd
                    ? `$${formatNumber(
                        conditionDetailData?.max_discount_value_usd
                      )}`
                    : ""}
                </p>
              ) : (
                <p className="text-gray-400">N/A</p>
              )}
            </div>
          </>
        )}
        {shouldShowFixedAmountDiscountValue(discountType) && (
          <>
            <div className="col-span-full sm:col-span-2">
              <Label id="discount_value" name="Discount Value" />
              <p>
                {conditionDetailData?.discount_value
                  ? `${formatNumber(conditionDetailData?.discount_value)}đ`
                  : ""}{" "}
                {conditionDetailData?.discount_value &&
                conditionDetailData?.discount_value_usd
                  ? "|"
                  : ""}{" "}
                {conditionDetailData?.discount_value_usd
                  ? `$${formatNumber(conditionDetailData?.discount_value_usd)}`
                  : ""}
              </p>
            </div>
          </>
        )}
      </>
    );
  };

  const isAccessPromotionCouponPath = () => {
    return location.pathname.includes('/promotion/coupon/');
  }

  const _redirectDetailCondition = (id: number) => {
    navigate(`/promotion/condition/${id}`);
  }

  return (
    <CollapsibleSection
      label="Condition Setting"
      defaultOpen={true}
      toolbar={
        <SwitchButton
          checked={conditionDetailData?.is_active}
          onChange={() => {}}
          disabled
        />
      }
    >
      <div className="w-full box-container">
        <div className="grid gap-x-4 gap-y-4 grid-cols-8 text-sm">
          <div className="col-span-full flex flex-col items-start">
            <Label id="note" name="Condition Name" />
            <div
              className={`${isAccessPromotionCouponPath() && "hover:cursor-pointer"}`}
              onClick={() =>
                isAccessPromotionCouponPath() &&
                _redirectDetailCondition(conditionDetailData?.id)
              }
            >
              <Tag text={conditionDetailData?.note ?? ""} type="blue" />
            </div>
          </div>
          {canAccessByType(UserType.ADMIN) && (
            <div
              className={
                conditionDetailData?.apply_to_product_type ===
                ApplyToProductType.ALL
                  ? "col-span-full"
                  : "col-span-full sm:col-span-4"
              }
            >
              <Label id="apply_to_product_type" name="Apply For" />
              {getApplyToProductTypeLabel(
                conditionDetailData?.apply_to_product_type
              )}
            </div>
          )}
          {shouldShowEventName() ? (
            <div
              className={`col-span-full ${
                canAccessByType(UserType.ADMIN) ? "sm:col-span-4" : ""
              }`}
            >
              <Label id="event_ids" name="Event Name" />
              <div className="flex items-center flex-wrap gap-2.5">
                {(conditionDetailData?.products ?? []).map((product) => (
                  <Tag text={product?.name_en ?? ""} key={product?.event_id} />
                ))}
              </div>
            </div>
          ) : (
            <></>
          )}
          <div className="col-span-full sm:col-span-4">
            <Label id="apply_from" name="Apply From" />
            <p>{getFormattedDateTime(conditionDetailData?.apply_from)}</p>
          </div>
          <div className="col-span-full sm:col-span-4">
            <Label id="apply_to" name="Apply To" />
            <p>{getFormattedDateTime(conditionDetailData?.apply_to)}</p>
          </div>
          <div className="col-span-full sm:col-span-4">
            <Label id="discount_type_display" name="Discount Type" />
            <p>
              {getDiscountTypeLabel(conditionDetailData?.discount_type_display)}
            </p>
          </div>
          {_renderDiscountFields(conditionDetailData?.discount_type_display)}
          {shouldShowApplyToLevel() ? (
            <div className="col-span-full sm:col-span-4">
              <Label id="apply_to_level" name="Apply To Level" />
              <p>{getApplyToLevelLabel(conditionDetailData?.apply_to_level)}</p>
            </div>
          ) : (
            <></>
          )}
          {shouldShowMinOrderValue() ? (
            <div className="col-span-full sm:col-span-2">
              <Label id="min_total" name="Min order value" />
              {conditionDetailData?.min_total ||
              conditionDetailData?.min_total_usd ? (
                <p>
                  {conditionDetailData?.min_total
                    ? `${conditionDetailData?.min_total}đ`
                    : ""}{" "}
                  {conditionDetailData?.min_total &&
                  conditionDetailData?.min_total_usd
                    ? "|"
                    : ""}{" "}
                  {conditionDetailData?.min_total_usd
                    ? `$${conditionDetailData?.min_total_usd}`
                    : ""}
                </p>
              ) : (
                <p className="text-gray-400">N/A</p>
              )}
            </div>
          ) : (
            <></>
          )}
          {shouldShowMaxApplicableItems() ? (
            <div className="col-span-full sm:col-span-4">
              <Label
                id="apply_max_item_on_cart_qty"
                name="Max Applicable Items"
              />
              <p>
                {formatNumber(conditionDetailData?.apply_max_item_on_cart_qty)}
              </p>
            </div>
          ) : (
            <></>
          )}
          {shouldShowTickets() ? (
            <>
              <div className="col-span-full">
                <div>
                  <Label id="apply_product_ids" name="Tickets" />
                  <div className="flex flex-col gap-y-4">
                    {(conditionDetailData?.products ?? []).map(
                      (product, index) => (
                        <div key={index}>
                          <p>{product?.name_en}</p>
                          <div className="flex items-center flex-wrap gap-2.5 mt-2">
                            {(product?.tickets ?? []).map((ticket) => (
                              <Tag
                                text={ticket?.name_en ?? ""}
                                key={ticket.id}
                              />
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <></>
          )}
          {shouldShowQuantityDiscountRules() && (
            <div className="col-span-full pt-4 border-t border-gray-300">
              <Label
                id="quantity_discount_rules"
                className="text-lg"
                name={
                  conditionDetailData?.discount_type_display ===
                  DiscountType.GROUP_DISCOUNT
                    ? "Group Discount Condition"
                    : "Quantity Discount Condition"
                }
              />
              {!isEmpty(conditionDetailData?.quantity_discount_rules) && (
                <div className="bg-white flex flex-col gap-y-4 mt-4">
                  {(conditionDetailData?.quantity_discount_rules ?? []).map(
                    (rule, index) => (
                      <div
                        key={index}
                        className="flex flex-col sm:flex-row gap-4 px-3 py-2 border border-magnolia-700 rounded-lg"
                      >
                        <div className="flex-1">
                          <Label
                            id={`quantity_discount_rules.${index}.qty`}
                            name="Quantity"
                          />
                          <p>{rule?.qty}</p>
                        </div>
                        <div className="flex-1">
                          <Label
                            id={`quantity_discount_rules.${index}.discount_type`}
                            name="Discount Type"
                          />
                          {getDiscountTypeLabel(rule?.discount_type)}
                        </div>
                        {rule?.discount_type === DiscountType.PERCENT ? (
                          <>
                            <div className="flex-1">
                              <Label
                                id="discount_value"
                                name="Discount Value"
                              />
                              <p>{rule?.discount_value}%</p>
                            </div>
                            <div className="flex-1">
                              <Label
                                id={`quantity_discount_rules.${index}.max_discount_value`}
                                name="Max discount amount"
                              />
                              {rule?.max_discount_value ||
                              rule?.max_discount_value_usd ? (
                                <p>
                                  {rule?.max_discount_value
                                    ? `${formatNumber(
                                        rule?.max_discount_value
                                      )}đ`
                                    : ""}{" "}
                                  {rule?.max_discount_value &&
                                  rule?.max_discount_value_usd
                                    ? "|"
                                    : ""}{" "}
                                  {rule?.max_discount_value_usd
                                    ? `$${formatNumber(
                                        rule?.max_discount_value_usd
                                      )}`
                                    : ""}
                                </p>
                              ) : (
                                <p className="text-gray-400">N/A</p>
                              )}
                            </div>
                          </>
                        ) : (
                          <></>
                        )}

                        {rule?.discount_type === DiscountType.FIX_PRICE ? (
                          <div className="flex-1">
                            <Label
                              id={`quantity_discount_rules.${index}.discount_value`}
                              name="Discount Value"
                            />
                            {rule?.discount_value ||
                            rule?.discount_value_usd ? (
                              <p>
                                {rule?.discount_value
                                  ? `${formatNumber(rule?.discount_value)}đ`
                                  : ""}{" "}
                                {rule?.discount_value &&
                                rule?.discount_value_usd
                                  ? "|"
                                  : ""}{" "}
                                {rule?.discount_value_usd
                                  ? `$${formatNumber(rule?.discount_value_usd)}`
                                  : ""}
                              </p>
                            ) : (
                              <></>
                            )}
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          )}
          {shouldShowCampaignDiscount() && (
            <>
              <div className="col-span-full pt-4 border-t border-gray-300">
                <Label
                  id="campaign_discount"
                  className="text-lg mb-0"
                  name="Campaign/Special Discount Condition"
                />
              </div>
              <div className="col-span-full sm:col-span-2">
                <Label id="campaign_code" name="Campaign Code" />
                <p>{conditionDetailData?.campaign_code}</p>
              </div>
              <div className="col-span-full sm:col-span-2">
                <Label id="discount_type" name="Discount Type" />
                <p>
                  {getDiscountTypeLabel(conditionDetailData?.discount_type)}
                </p>
              </div>
              {_renderDiscountFields(conditionDetailData?.discount_type)}
            </>
          )}
        </div>
      </div>
    </CollapsibleSection>
  );
};
